import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { FiCreditCard, FiCalendar, FiClock, FiShield, FiCheckCircle, FiAlertCircle, FiXCircle, FiFileText } from 'react-icons/fi';
import { Card, Button, Loading, AlertModal, ConfirmDialog } from '../components/common';
import Navbar from '../components/Navbar';
import SignaturePad from '../components/SignaturePad';
import { formatDateTime } from '../data/carsData';
import { useAuth } from '../context/AuthContext';
import { createBooking } from '../firebase/bookings';
import { getUserById } from '../firebase/users';

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, refreshUserProfile } = useAuth();
  const [bookingData, setBookingData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('phonepe');
  const [isProcessing, setIsProcessing] = useState(false);
  const [userDocuments, setUserDocuments] = useState(null);
  const [checkingDocuments, setCheckingDocuments] = useState(true);
  const [userProfileData, setUserProfileData] = useState(null);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [signature, setSignature] = useState(null);
  const [alert, setAlert] = useState({ isOpen: false, type: 'success', title: '', message: '' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null, title: '', type: 'warning', confirmText: 'Confirm', cancelText: 'Cancel' });

  useEffect(() => {
    if (!location.state) {
      navigate('/');
      return;
    }
    setBookingData(location.state);
    
    // Check user documents and profile
    const checkDocuments = async () => {
      if (user?.uid) {
        try {
          const result = await getUserById(user.uid);
          if (result.user) {
            setUserDocuments(result.user.documents || {});
            setUserProfileData(result.user);
          }
        } catch (error) {
          console.error('Error checking documents:', error);
        }
      }
      setCheckingDocuments(false);
    };
    
    checkDocuments();
  }, [location, navigate, user]);

  // Check if all documents are uploaded
  const hasAllDocuments = () => {
    return userDocuments?.['license-front']?.url &&
           userDocuments?.['license-back']?.url &&
           userDocuments?.['aadhar-front']?.url;
  };

  // Check if all documents are verified
  const areAllDocumentsVerified = () => {
    return userDocuments?.['license-front']?.verified &&
           userDocuments?.['license-back']?.verified &&
           userDocuments?.['aadhar-front']?.verified;
  };

  // Merge profile from fetched data and AuthContext (userProfile may have fresher data)
  const effectiveProfile = userProfileData || userProfile;

  // Check if required profile fields are filled
  const hasRequiredProfileFields = () => {
    if (!effectiveProfile) return false;
    const name = effectiveProfile.displayName || effectiveProfile.name;
    return name &&
           effectiveProfile.phone &&
           effectiveProfile.aadharNumber &&
           effectiveProfile.address;
  };

  // Get missing profile fields
  const getMissingProfileFields = () => {
    const missing = [];
    if (!effectiveProfile?.displayName && !effectiveProfile?.name) missing.push('Full Name');
    if (!effectiveProfile?.phone) missing.push('Phone Number');
    if (!effectiveProfile?.aadharNumber) missing.push('Aadhar Number');
    if (!effectiveProfile?.address) missing.push('Address');
    return missing;
  };

  const handlePayment = async () => {
    if (!user) {
      navigate('/login', { state: { from: '/payment' } });
      return;
    }

    // Fetch latest profile from Firestore (in case user updated in another tab)
    let freshProfile = userProfileData || userProfile || {};
    try {
      await refreshUserProfile?.();
      const result = await getUserById(user.uid);
      if (result.user) {
        freshProfile = result.user;
        setUserProfileData(result.user);
      }
    } catch (e) {
      // Continue with existing data
    }

    // When documents are uploaded, allow booking with available profile data.
    // Only require profile when documents are not uploaded (handled earlier).
    const name = freshProfile?.displayName || freshProfile?.name || user?.displayName || user?.email?.split('@')[0] || 'Customer';
    const phone = freshProfile?.phone || user?.phoneNumber || '';
    const aadharNumber = freshProfile?.aadharNumber || '';
    const address = freshProfile?.address || '';

    // Check if documents are uploaded
    if (!hasAllDocuments()) {
      setConfirmDialog({
        isOpen: true,
        title: 'Documents Required',
        message: 'Please upload all required documents (License front/back and Aadhar front) before booking. Would you like to go to your profile to upload them now?',
        type: 'warning',
        confirmText: 'Go to Profile',
        cancelText: 'Cancel',
        onConfirm: () => {
          navigate('/profile');
        }
      });
      return;
    }

    // Check if terms are agreed
    if (!termsAgreed) {
      setAlert({
        isOpen: true,
        type: 'warning',
        title: 'Terms & Conditions Required',
        message: 'Please read and agree to the terms and conditions to proceed.'
      });
      return;
    }

    // Check if signature is provided
    if (!signature) {
      setAlert({
        isOpen: true,
        type: 'warning',
        title: 'Signature Required',
        message: 'Please provide your digital signature to proceed.'
      });
      return;
    }

    setIsProcessing(true);
    
    try {
      // Simulate payment processing delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Prepare booking data for Firebase
      const bookingDataToSave = {
        companyId: car.companyId, // Get companyId from car
        carId: car.id,
        userId: user.uid,
        fromDateTime: fromDateTime,
        toDateTime: toDateTime,
        duration: duration,
        durationHours: durationHours,
        costBreakdown: costBreakdown,
        subtotal: subtotal,
        serviceChargePercent: serviceChargePercent,
        serviceCharge: serviceCharge,
        serviceChargeEnabled: serviceChargeEnabled,
        gstPercent: gstPercent,
        gst: gst,
        gstEnabled: gstEnabled,
        totalAmount: finalTotal,
        status: 'pending',
        paymentStatus: 'pending',
        carDetails: {
          name: car.name,
          brand: car.brand,
          model: car.model,
          image: car.images?.[0] || car.image || ''
        },
        customerDetails: {
          name: name,
          email: user.email || '',
          phone: phone,
          aadharNumber: aadharNumber,
          alternatePhone: freshProfile?.alternatePhone || '',
          whatsappNumber: freshProfile?.whatsappNumber || '',
          address: address
        },
        termsAgreed: true,
        signature: {
          image: signature,
          signedAt: new Date().toISOString(),
          signedBy: user.uid
        },
        termsVersion: company?.termsVersion || '1.0'
      };
      
      // Create booking in Firebase
      const result = await createBooking(bookingDataToSave);
      
      if (result.error) {
        setAlert({
          isOpen: true,
          type: 'error',
          title: 'Booking Failed',
          message: result.error || 'Failed to create booking. Please try again.'
        });
        setIsProcessing(false);
        return;
      }
      
      // Navigate to success page with booking ID
      navigate('/success', { 
        state: { 
          ...bookingData, 
          bookingId: result.id || result.bookingNumber,
          bookingNumber: result.bookingNumber,
          finalTotal 
        } 
      });
    } catch (error) {
      console.error('Error creating booking:', error);
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Booking Failed',
        message: 'Failed to create booking. Please try again.'
      });
      setIsProcessing(false);
    }
  };

  if (!bookingData) {
    return <Loading fullScreen text="Loading payment details..." />;
  }

  const { car, company, fromDateTime, toDateTime, duration, durationHours, costBreakdown, totalAmount } = bookingData;

  const subtotal = totalAmount || 0;
  
  // Get pricing configuration from company (or use defaults if not available)
  const serviceChargePercent = company?.serviceChargePercent || 0;
  const serviceChargeEnabled = company?.serviceChargeEnabled !== false; // Default to true if not set
  const gstPercent = company?.gstPercent || 0;
  const gstEnabled = company?.gstEnabled !== false; // Default to true if not set
  
  // Calculate charges only if enabled
  const serviceCharge = serviceChargeEnabled && serviceChargePercent > 0 
    ? (subtotal * serviceChargePercent / 100) 
    : 0;
  const gst = gstEnabled && gstPercent > 0 
    ? (subtotal * gstPercent / 100) 
    : 0;
  
  const finalTotal = subtotal + serviceCharge + gst;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Complete Your Booking</h1>
          <p className="text-sm sm:text-base text-gray-600">Review your booking details and proceed to payment</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Car Details */}
            <Card className="p-4 sm:p-6 border-0 shadow-soft">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Booking Summary</h2>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
                <img
                  src={car.images?.[0] || car.image || ''}
                  alt={car.name}
                  className="w-full sm:w-32 h-32 sm:h-24 object-contain bg-gray-100 rounded-xl"
                />
                <div className="flex-1">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">{car.name}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mb-2">{car.brand} • {car.model}</p>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                    <FiCalendar className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 mb-1">From</p>
                    <p className="font-semibold text-gray-900">{formatDateTime(fromDateTime)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                    <FiCalendar className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 mb-1">To</p>
                    <p className="font-semibold text-gray-900">{formatDateTime(toDateTime)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                    <FiClock className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 mb-1">Duration</p>
                    <p className="font-semibold text-gray-900">{duration}</p>
                    {costBreakdown && (
                      <p className="text-sm text-gray-600 mt-1">
                        {costBreakdown.slots24Hour > 0 && `${costBreakdown.slots24Hour} × 24hr`}
                        {costBreakdown.slots24Hour > 0 && costBreakdown.slots12Hour > 0 && ' + '}
                        {costBreakdown.slots12Hour > 0 && `${costBreakdown.slots12Hour} × 12hr`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* Document Status */}
            {checkingDocuments ? (
              <Card className="p-4 sm:p-6 border-0 shadow-soft">
                <div className="flex items-center justify-center py-4">
                  <Loading />
                </div>
              </Card>
            ) : (
              <Card className="p-4 sm:p-6 border-0 shadow-soft">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                  <FiShield className="w-5 h-5" />
                  Document Verification
                </h2>
                
                {!hasAllDocuments() ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <FiXCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-red-900 mb-1">Documents Required</p>
                        <p className="text-sm text-red-700 mb-3">
                          Please upload all required documents (License front/back and Aadhar front) before proceeding with the booking.
                        </p>
                        <Link to="/profile">
                          <Button variant="outline" size="sm" className="w-full sm:w-auto">
                            Upload Documents
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : areAllDocumentsVerified() ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <FiCheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-900 mb-1">All Documents Verified</p>
                        <p className="text-sm text-green-700">
                          Your documents have been verified. You can proceed with the booking.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <FiAlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-yellow-900 mb-1">Documents Pending Verification</p>
                        <p className="text-sm text-yellow-700">
                          Your documents have been uploaded and are pending verification. You can proceed with the booking, but verification will be completed at the time of car pickup.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Payment Method */}
            <Card className="p-4 sm:p-6 border-0 shadow-soft">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Payment Method</h2>
              
              <div className="space-y-2 sm:space-y-3">
                <button
                  onClick={() => setPaymentMethod('phonepe')}
                  disabled
                  className="w-full p-3 sm:p-4 border-2 rounded-xl text-left transition-all border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                >
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 border-gray-300 flex items-center justify-center flex-shrink-0">
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm sm:text-base text-gray-900">PhonePe</p>
                      <p className="text-xs sm:text-sm text-gray-500">Coming Soon</p>
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FiCreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setPaymentMethod('card')}
                  disabled
                  className="w-full p-4 border-2 rounded-xl text-left transition-all border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex items-center justify-center">
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">Credit/Debit Card</p>
                      <p className="text-sm text-gray-500">Coming Soon</p>
                    </div>
                    <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                      <FiCreditCard className="w-6 h-6 text-gray-400" />
                    </div>
                  </div>
                </button>
              </div>

              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Payment methods are currently disabled. Your reservation will be sent to the manager for approval.
                </p>
              </div>
            </Card>

            {/* Terms & Conditions */}
            <Card className="p-4 sm:p-6 border-0 shadow-soft">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FiFileText className="w-5 h-5" />
                Terms & Conditions
              </h2>

              <div className="mb-4 p-4 bg-gray-50 rounded-lg max-h-96 overflow-y-auto">
                <div className="space-y-3 text-sm text-gray-700">
                  <p className="font-semibold text-gray-900">Please read and agree to the following terms:</p>
                  <ol className="list-decimal list-inside space-y-2 ml-2">
                    <li>You must capture scratches of the particular car yourself before taking the vehicle.</li>
                    <li>For every extra kilometer beyond the package limit: 5 seater - ₹6/km, 7 seater - ₹8/km will be charged.</li>
                    <li>For every extra hour beyond the package: 5 seater - ₹250/hr, 7 seater - ₹350/hr will be charged.</li>
                    <li>Fuel is not included in the rental package.</li>
                    <li>Fuel level should be the same while returning the vehicle.</li>
                    <li>Insurance of the particular vehicle will not be provided. Each and every rupee and daily rent should be borne by the customer in case of any accident or damage.</li>
                    <li>You must inform us 1 hour before returning the vehicle.</li>
                    <li>The proofs submitted by you will be returned after checking the vehicle thoroughly by our assistant.</li>
                    <li>If you face any inconvenience with our service, please feel free to report to our assistants.</li>
                    <li>You are not allowed to use the vehicle for illegal activities. In case of misuse, you will be liable to pay fines according to the Government act.</li>
                  </ol>
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAgreed}
                  onChange={(e) => setTermsAgreed(e.target.checked)}
                  className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  I hereby agree to all terms and conditions mentioned above <span className="text-red-500">*</span>
                </span>
              </label>
            </Card>

            {/* Digital Signature */}
            <Card className="p-4 sm:p-6 border-0 shadow-soft">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FiFileText className="w-5 h-5" />
                Digital Signature <span className="text-red-500">*</span>
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Please sign below using your mouse or touch screen. You can re-sign until the booking is sent for approval.
              </p>
              <SignaturePad
                onSave={(sig) => setSignature(sig)}
                onClear={() => setSignature(null)}
                initialSignature={signature}
                disabled={false}
              />
            </Card>
          </div>

          {/* Right Column - Bill Summary */}
          <div className="lg:col-span-1">
            <Card className="p-4 sm:p-6 sticky top-20 sm:top-24 border-0 shadow-medium">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Bill Summary</h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium text-gray-900">₹{subtotal.toLocaleString()}</span>
                </div>
                {gstEnabled && gstPercent > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">GST ({gstPercent}%)</span>
                    <span className="font-medium text-gray-900">₹{gst.toFixed(2)}</span>
                  </div>
                )}
                {serviceChargeEnabled && serviceChargePercent > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Service Charge ({serviceChargePercent}%)</span>
                    <span className="font-medium text-gray-900">₹{serviceCharge.toFixed(2)}</span>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-3 sm:pt-4 mb-4 sm:mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-base sm:text-lg font-bold text-gray-900">Total</span>
                  <span className="text-2xl sm:text-3xl font-bold text-primary-600">
                    ₹{finalTotal.toFixed(2)}
                  </span>
                </div>
              </div>

              <Button
                fullWidth
                size="lg"
                onClick={handlePayment}
                isLoading={isProcessing}
                disabled={isProcessing || !hasAllDocuments() || !termsAgreed || !signature}
                className="gradient-primary text-white shadow-soft mb-3 sm:mb-4 text-sm sm:text-base"
              >
                {isProcessing 
                  ? 'Processing...' 
                  : !hasAllDocuments()
                    ? 'Upload Documents First'
                    : !termsAgreed
                      ? 'Agree to Terms'
                      : !signature
                        ? 'Provide Signature'
                        : `Reserve ₹${finalTotal.toFixed(2)}`}
              </Button>

              <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <FiShield className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-900">Secure Payment</p>
                    <p className="text-xs text-green-700 mt-1">Your payment is secure and encrypted</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        type={alert.type}
        title={alert.title}
        message={alert.message}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
      />
    </div>
  );
};

export default Payment;
